// context/SocketContext.tsx

import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { io, Socket } from "socket.io-client";
import { useAuth } from "./AuthContext";
import { useDispatch, useSelector } from "react-redux";
import { setPublicToken } from "@/redux/slices/chat.slice";

const SOCKET_URL = `${import.meta.env.VITE_PUBLIC_BASE_URL}`;

interface SocketContextProps {
  socket: Socket | null;
  connected: boolean;
  disconnect: () => void;
  ensureConnected: () => void;
}

const SocketContext = createContext<SocketContextProps | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider = ({ children }: SocketProviderProps) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const dispatch = useDispatch();
  const [connected, setConnected] = useState(false);
  const { user, token } = useAuth();
  useEffect(() => {
    dispatch(setPublicToken(token));
  }, [token]);

  const publicToken = useSelector((state) => state.chat.publicToken);

  const userId = user?._id;

  const disconnect = () => {
    if (socket) {
      socket.disconnect();
      console.log("Socket disconnected");
    }
    setSocket(null);
    setConnected(false);
  };

  const ensureConnected = () => {
    if (socket && !socket.connected) {
      console.log("Reconnecting socket...");
      socket.connect();
    }
  };

  useEffect(() => {
    if (!publicToken) return;

    const mainSocket = io(SOCKET_URL, {
      transports: ["websocket"],
      query: { publicToken },
      autoConnect: true,
    });

    mainSocket.on("connect", () => {
      console.log("Socket connected");
      setConnected(true);
      setSocket(mainSocket);
      mainSocket.emit("connect-user");
    });

    mainSocket.on("disconnect", () => {
      console.log("Socket disconnected");
      setConnected(false);
    });

    mainSocket.on("connect_error", (err) => {
      console.error("Socket connection error:", err);
    });

    return () => {
      mainSocket.disconnect();
    };
  }, [publicToken]);

  return (
    <SocketContext.Provider
      value={{
        socket,
        connected,
        disconnect,
        ensureConnected,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
